import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ApiError, api } from './api';
import { BrandIdentity } from './components/BrandIdentity';
import { ChatTour } from './components/ChatTour';
import { PushBridge } from './components/PushBridge';
import { RunNotifications, type RunSource } from './components/RunNotifications';
import { ShareIntake } from './components/ShareIntake';
import { Sidebar } from './components/Sidebar';
import { TooltipLayer } from './components/TooltipLayer';
import { useBranding } from './hooks/useBranding';
import { useDrawerFocus } from './hooks/useDrawerFocus';
import { useKeyboardInset } from './hooks/useKeyboardInset';
import { detectBrowserLanguage, LanguageProvider, translate } from './i18n';
import { useDrafts } from './stores/drafts';
import { useUi } from './stores/ui';
import { applyTheme } from './theme';

/**
 * Auth bootstrap. Hits `/api/me` once at startup; if the call fails with 401
 * we redirect to /login. While the call is in flight we render a thin
 * placeholder rather than flashing the chat shell.
 */
export function App() {
  const branding = useBranding();
  const location = useLocation();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  useKeyboardInset();
  const menuButton = useRef<HTMLButtonElement>(null);
  const theme = useUi((s) => s.theme);
  const textSize = useUi((s) => s.textSize);
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: false,
    refetchInterval: 30_000,
  });
  const ownerId = useDrafts((state) => state.ownerId);
  useEffect(() => {
    if (me.data?.user.id) useDrafts.getState().bindOwner(me.data.user.id);
  }, [me.data?.user.id]);
  const language = me.data?.user.preferredLanguage ?? detectBrowserLanguage();

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset.textSize = textSize;
  }, [textSize]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    setMobileNavigationOpen(false);
  }, [location.pathname]);

  useDrawerFocus(mobileNavigationOpen, '#primary-navigation', '.nv-navclose', menuButton);

  useEffect(() => {
    if (!mobileNavigationOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavigationOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileNavigationOpen]);

  if (me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-13 text-text-tertiary">
        {translate(language, 'loading')}
      </div>
    );
  }
  if (me.error instanceof ApiError && me.error.status === 401) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (me.error && !me.data) {
    return (
      <div className="flex h-full items-center justify-center text-13 text-danger">
        {translate(language, 'backendUnavailable')}
      </div>
    );
  }

  const user = me.data?.user;
  const product = me.data?.product;
  if (!user || !product) return <Navigate to="/login" replace />;
  if (ownerId !== user.id) return null;
  const runSources: RunSource[] = [
    ...(product.capabilities.agents && user.agentsEnabled ? (['agents'] as const) : []),
    ...(product.capabilities.automations ? (['automations'] as const) : []),
  ];

  return (
    <LanguageProvider language={language}>
      <div className="nv-root nv-shell">
        <TooltipLayer />
        <ChatTour user={user} product={product} />
        <PushBridge userId={user.id} />
        <ShareIntake limits={me.data?.attachmentLimits} />
        {runSources.length > 0 && <RunNotifications userId={user.id} sources={runSources} />}
        {mobileNavigationOpen && (
          <button
            type="button"
            className="nv-backdrop"
            aria-label={translate(language, 'closeNavigation')}
            onClick={() => setMobileNavigationOpen(false)}
          />
        )}
        <Sidebar
          branding={branding}
          user={user}
          product={product}
          mobileOpen={mobileNavigationOpen}
          onMobileClose={() => setMobileNavigationOpen(false)}
        />
        <main className="nv-main nv-chatmain">
          <header className="nv-mhead">
            <button
              type="button"
              ref={menuButton}
              className="nv-mbtn"
              aria-controls="primary-navigation"
              aria-expanded={mobileNavigationOpen}
              aria-label={translate(language, 'openNavigation')}
              title={translate(language, 'openNavigation')}
              onClick={() => setMobileNavigationOpen(true)}
            >
              <MenuIcon />
            </button>
            <BrandIdentity branding={branding} product="chat" />
          </header>
          <div className="nv-outlet">
            <Outlet context={product} />
          </div>
        </main>
      </div>
    </LanguageProvider>
  );
}

function MenuIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
      width="18"
      height="18"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
